import React from "react";
import Head from "next/head";
import BaseWineSearchBar from "../../../../components/global/SearchBar/BaseWineSearchBar";
import ArticleWines from "../../../../components/global/Articles/ArticleWines";
import Header from "../../../../components/layout/Header/Header";
import Layout from "../../../../components/layout/Layout/Layout";
import { getArticleFromSlug } from "../../../../lib/articleRequests";
import { formatTitle } from "../../../../utils/helper";

const reviewsJsonLd = (article, slug) =>
  JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      url: `https://vinous.com/articles/${slug}/reviews`,
      ...(article.preview && { description: article.preview }),
      ...(article.author && {
        author: { "@type": "Person", name: article.author },
      }),
      isAccessibleForFree: false,
      hasPart: [
        {
          "@type": "WebPageElement",
          cssSelector: ".wine-score-paywall",
          isAccessibleForFree: false,
        },
        {
          "@type": "WebPageElement",
          cssSelector: ".wine-review-paywall",
          isAccessibleForFree: false,
        },
      ],
    },
    null,
    2
  );

export default function ArticleWinesPage({ article, slug }) {
  return (
    <Layout disableMeta={!!article}>
      {article && (
        <Head>
          <title>{formatTitle(`${article.title} - Reviews`)}</title>
          <meta name="description" content={article.preview} />
          {article.author && <meta name="author" content={article.author} />}
          {article.keywords?.length > 0 && (
            <meta name="keywords" content={article.keywords.join(",")} />
          )}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link
            rel="canonical"
            href={`https://vinous.com/articles/${slug}/reviews`}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: reviewsJsonLd(article, slug) }}
            key="article-reviews-jsonld"
          />
        </Head>
      )}
      <Header />
      <BaseWineSearchBar className="pb-8" hideResults={true} />
      <ArticleWines article={article} />
    </Layout>
  );
}

export const getServerSideProps = async (context) => {
  const { slug } = context.params;

  try {
    const { data } = await getArticleFromSlug(slug as string);
    const article = data.article;

    if (article.published === false) {
      return {
        redirect: {
          destination: "/",
          permanent: false,
        },
      };
    }

    return {
      props: {
        article,
        slug,
      },
    };
  } catch (error) {
    if (error?.data?.error === "Article not found") {
      return {
        notFound: true,
      };
    }

    // Metadata is a nicety here - if the article lookup fails, still render the
    // page and let Layout supply its default meta rather than 500ing.
    return {
      props: {
        article: null,
        slug,
      },
    };
  }
};
